import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, MailWarning, MessageSquare, Plus } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "cnc_download_token";

export default function Forum() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [member, setMember] = useState(false);
  const [checking, setChecking] = useState(!!localStorage.getItem(STORAGE_KEY));
  const [threads, setThreads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [gateMsg, setGateMsg] = useState("");
  const [devLink, setDevLink] = useState("");

  const loadThreads = async (tok) => {
    try {
      const { data } = await api.get("/forum/threads", { params: { token: tok } });
      setThreads(data.threads);
    } catch {
      setThreads([]);
    }
  };

  useEffect(() => {
    if (!token) return setChecking(false);
    api
      .get("/session", { params: { token } })
      .then(({ data }) => {
        if (data.valid && data.verified) {
          setMember(true);
          loadThreads(token);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setToken("");
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gate = async (e) => {
    e.preventDefault();
    setGateMsg("");
    setDevLink("");
    try {
      const { data } = await api.post("/signup", { email });
      if (data.status === "already_verified") {
        localStorage.setItem(STORAGE_KEY, data.token);
        setToken(data.token);
        setMember(true);
        loadThreads(data.token);
      } else {
        setGateMsg(data.message);
        setDevLink(data.dev_verification_url || "");
      }
    } catch (err) {
      setGateMsg(err.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  const postThread = async (e) => {
    e.preventDefault();
    setPosting(true);
    setError("");
    try {
      await api.post("/forum/threads", { token, title, body, name: name || undefined });
      setTitle("");
      setBody("");
      setShowForm(false);
      await loadThreads(token);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not post. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-[#F5F5F5] blueprint-grid px-6 py-16" data-testid="forum-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="label-mono hover:text-[#FF3B00] transition-colors flex items-center gap-2" data-testid="forum-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> CNCroutersoft
        </Link>
        <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl mt-8">Workshop forum</h1>
        <p className="mt-3 text-white/60 text-sm">Members only — help each other out, suggest improvements.</p>

        {checking ? (
          <div className="mt-12 flex items-center gap-3" data-testid="forum-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#FF3B00]" />
            <span className="font-mono2 text-sm text-white/60">Checking membership…</span>
          </div>
        ) : !member ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 border border-white/15 bg-black/60 p-8" data-testid="forum-gate">
            <p className="label-mono mb-3 !text-[#FF3B00]">Registered downloaders only</p>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              Enter the email you registered with. Already confirmed? You're straight in. New here — confirm once and the forum opens.
            </p>
            <form onSubmit={gate} className="flex flex-col sm:flex-row gap-3" data-testid="forum-gate-form">
              <input
                data-testid="forum-gate-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@workshop.com"
                className="flex-1 bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
              />
              <button data-testid="forum-gate-submit" type="submit" className="btn-forge">Enter forum</button>
            </form>
            {gateMsg && <p className="mt-3 font-mono2 text-xs text-white/60" data-testid="forum-gate-message">{gateMsg}</p>}
            {devLink && (
              <div className="mt-4 border border-dashed border-[#F5A623]/50 bg-[#F5A623]/5 p-4" data-testid="forum-mock-inbox">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F5A623] mb-2 flex items-center gap-2">
                  <MailWarning className="w-3.5 h-3.5" /> Mocked inbox — confirm via this link
                </p>
                <a href={devLink} className="font-mono2 text-xs text-white/80 break-all underline decoration-[#FF3B00] underline-offset-4 hover:text-[#FF3B00] transition-colors" data-testid="forum-mock-link">{devLink}</a>
              </div>
            )}
          </motion.div>
        ) : (
          <div className="mt-10" data-testid="forum-content">
            <div className="flex items-center justify-between mb-6">
              <p className="label-mono">{threads.length} thread{threads.length === 1 ? "" : "s"}</p>
              <button data-testid="forum-new-thread-button" onClick={() => setShowForm(!showForm)} className="btn-forge !px-5 !py-3">
                <Plus className="w-4 h-4" /> New thread
              </button>
            </div>

            {showForm && (
              <motion.form initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} onSubmit={postThread} className="border border-white/15 bg-black/60 p-6 mb-8 space-y-4" data-testid="forum-new-form">
                <input
                  data-testid="forum-thread-title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Thread title"
                  className="w-full bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
                />
                <textarea
                  data-testid="forum-thread-body"
                  required
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="What's the issue or idea?"
                  className="w-full bg-transparent border border-white/25 focus:border-[#FF3B00] outline-none p-3 font-mono2 text-sm placeholder:text-white/25 transition-colors resize-none"
                />
                <input
                  data-testid="forum-display-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Display name (optional, saved after first post)"
                  className="w-full bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
                />
                {error && <p className="font-mono2 text-xs text-[#FF3B00]" data-testid="forum-error">{error}</p>}
                <button data-testid="forum-post-button" type="submit" disabled={posting} className="btn-forge">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post thread"}
                </button>
              </motion.form>
            )}

            {threads.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center" data-testid="forum-empty">
                <MessageSquare className="w-8 h-8 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 text-sm">No threads yet — start the first one.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {threads.map((t) => (
                  <Link
                    key={t.id}
                    to={`/forum/${t.id}`}
                    data-testid={`forum-thread-${t.id}`}
                    className="block grid-cell p-5 hover:border-[#FF3B00]/50 transition-colors"
                  >
                    <p className="font-display uppercase tracking-tight text-lg">{t.title}</p>
                    <p className="font-mono2 text-[11px] text-white/40 mt-2">
                      {t.author_name} · {t.replies} {t.replies === 1 ? "reply" : "replies"} · last activity {t.last_activity?.slice(0, 16).replace("T", " ")}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
